import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import {
  JobQuote,
  QuotationPdfFile,
  fetchJobQuotePdf,
  setJobQuoteApproval,
  setJobQuoteWorkOrder,
} from '../../admin/admin.api';
import { useQuotationRights } from '../../../auth/access';
import { logError } from '../../../utils/logger';
import { formatAmount } from '../../shared/utils/misc';
import QuotationPdfViewer from './QuotationPdfViewer';

/**
 * The quotations already raised against a job, newest first.
 *
 * A job's quotations are append-only: each save raises a new one and the newest is the
 * quote in force, so this is a history rather than an editable list. The first row is
 * marked as such — every figure the Work Job Report shows for this job comes from it.
 *
 * Each row opens its own offer letter as a PDF, including the superseded ones: what was
 * sent to a client is worth being able to read back, and the server renders each letter
 * from the quotation it belongs to rather than from the one in force. The letter opens in
 * a viewer on this page rather than in another tab, so it stays next to the row it came
 * from.
 */

/** Reads the server's ISO timestamp as a date; undated rows sort last rather than crash. */
function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The two versions of each letter: on the firm's letterhead, and plain for printing on
 * its pre-printed stationery, where the letterhead's space is left blank.
 */
const PDF_VERSIONS = [
  {
    key: 'letterhead',
    withLetterhead: true,
    icon: 'mdi:file-pdf-box',
    tooltip: 'View quotation PDF with letterhead',
  },
  {
    key: 'plain',
    withLetterhead: false,
    icon: 'mdi:file-document-outline',
    tooltip: 'View quotation PDF without letterhead (for pre-printed letterhead paper)',
  },
] as const;

export default function JobQuotationList({
  quotes,
  onChanged,
  focusQuoteId,
}: {
  quotes: JobQuote[];
  onChanged?: () => void;
  /** A quotation to pick out — the one an approval notification pointed at. */
  focusQuoteId?: number | null;
}) {
  /**
   * V36: Accounts or the admin adds the client's work order; only the admin approves, and only
   * a quotation that has one — as the server enforces.
   */
  const { canRaise, canApprove } = useQuotationRights();
  const [approving, setApproving] = useState<number | null>(null);
  /** The quotation whose work order is being entered; null keeps the dialog closed. */
  const [workOrderFor, setWorkOrderFor] = useState<JobQuote | null>(null);

  async function toggleApproval(quote: JobQuote, approve: boolean) {
    setApproving(quote.id);
    try {
      await setJobQuoteApproval(quote.id, approve);
      toast.success(approve ? 'Quotation approved — bills can now be raised from it' : 'Approval withdrawn');
      onChanged?.();
    } catch (err) {
      toast.error(logError({ message: 'Could not change the approval', error: err, code: 'JOB_QUOTE_APPROVAL_FAILED' }));
    } finally {
      setApproving(null);
    }
  }

  /** The PDF being fetched, so only its own icon shows a spinner. */
  const [opening, setOpening] = useState<{ id: number; withLetterhead: boolean } | null>(null);
  /** The letter on screen, and which row it came from. null means the viewer is closed. */
  const [viewing, setViewing] = useState<{
    file: QuotationPdfFile;
    quote: JobQuote;
    withLetterhead: boolean;
  } | null>(null);

  /**
   * The object URL this list currently owns.
   *
   * `fetchJobQuotePdf` hands ownership over and nothing else revokes it, so without this
   * reading several letters would hold every one of them until the tab closed.
   *
   * Deliberately a ref rather than an effect keyed on `viewing`: under StrictMode React
   * runs an effect's cleanup immediately after mounting it in development, which would
   * revoke the URL the moment the viewer opened and leave an empty frame. Ownership ends
   * when the letter is replaced or closed, not when a render happens.
   */
  const ownedUrl = useRef<string | null>(null);

  function releaseOwnedUrl() {
    if (!ownedUrl.current) return;
    URL.revokeObjectURL(ownedUrl.current);
    ownedUrl.current = null;
  }

  /** The last letter read is still held open; free it when this list goes away. */
  useEffect(
    () => () => {
      if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current);
    },
    []
  );

  function closeViewer() {
    releaseOwnedUrl();
    setViewing(null);
  }

  async function viewPdf(quote: JobQuote, withLetterhead: boolean) {
    // Ignore a second click on a row already fetching; the endpoint renders on every
    // request and a double-click would otherwise fetch the same letter twice.
    if (opening !== null) return;
    setOpening({ id: quote.id, withLetterhead });
    try {
      const file = await fetchJobQuotePdf(quote.id, withLetterhead);
      releaseOwnedUrl();          // drop the previous letter, if one is still held
      ownedUrl.current = file.url;
      setViewing({ file, quote, withLetterhead });
    } catch (err) {
      // logError returns the message it settled on, including the API's own where there
      // was one, so the toast says what actually went wrong.
      toast.error(
        logError({
          message: 'Could not open the quotation PDF',
          error: err,
          code: 'JOB_QUOTE_PDF_FAILED',
        })
      );
    } finally {
      setOpening(null);
    }
  }

  return (
    <Box className='column gap-2'>
      <Stack direction='row' alignItems='center' gap={1.5}>
        <Typography variant='body1' color='text.primary'>
          Quotations
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {quotes.length === 0
            ? 'None raised yet'
            : `${quotes.length} raised · newest is in force`}
        </Typography>
      </Stack>

      {quotes.length === 0 ? (
        <Typography variant='body2' color='text.disabled'>
          No quotation has been raised for this job yet. Fill in the form below and submit
          to raise the first one.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Raised</TableCell>
                <TableCell>Quote as Per</TableCell>
                <TableCell align='right'>Total</TableCell>
                <TableCell align='right'>Discount</TableCell>
                <TableCell align='right'>Gross Total</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Work Order</TableCell>
                <TableCell>Approval</TableCell>
                <TableCell align='center'>PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((quote, index) => (
                <TableRow key={quote.id} hover selected={quote.id === focusQuoteId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction='row' alignItems='center' gap={1}>
                      {/* The letter's own date where one was chosen (V35), else the day it was raised. */}
                      {formatWhen(quote.quoteDate ?? quote.createdAt)}
                      {index === 0 && (
                        <Chip label='Current' size='small' color='primary' variant='outlined' />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{quote.quoteAsPer || '—'}</TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {formatAmount(quote.totalCost as number)}
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {formatAmount(quote.discount as number)}
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {formatAmount(quote.grossTotal as number)}
                  </TableCell>
                  {/* Notes can be long; the row stays one line and the full text is on hover. */}
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Box
                      component='span'
                      title={quote.notes ?? ''}
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {quote.notes || '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {quote.workOrderNo ? (
                      <Stack direction='row' alignItems='center' gap={0.5}>
                        <Tooltip title={`Added by ${quote.workOrderByName ?? '—'} on ${formatWhen(quote.workOrderAt)}`}>
                          <Box component='span'>
                            {quote.workOrderNo}
                            {quote.workOrderDate && (
                              <Typography component='span' variant='caption' color='text.secondary'>
                                {' '}· {formatWhen(quote.workOrderDate)}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                        {canRaise && !quote.approved && (
                          <Tooltip title='Correct the work order'>
                            <IconButton size='small' onClick={() => setWorkOrderFor(quote)} aria-label='Edit work order'>
                              <Icon icon='mdi:pencil-outline' width={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : canRaise && !quote.approved ? (
                      <Button size='small' variant='outlined' onClick={() => setWorkOrderFor(quote)}>
                        Add work order
                      </Button>
                    ) : (
                      <Typography variant='caption' color='text.secondary'>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {quote.approved ? (
                      <Stack direction='row' alignItems='center' gap={1}>
                        <Tooltip title={`Approved by ${quote.approvedByName ?? '—'} on ${formatWhen(quote.approvedAt)}`}>
                          <Chip label='Approved' size='small' color='success' />
                        </Tooltip>
                        {canApprove && (
                          <Button size='small' color='inherit' disabled={approving !== null}
                            onClick={() => toggleApproval(quote, false)}>
                            Withdraw
                          </Button>
                        )}
                      </Stack>
                    ) : !quote.workOrderNo ? (
                      <Typography variant='caption' color='text.secondary'>Awaiting work order</Typography>
                    ) : canApprove ? (
                      <Button size='small' variant='contained' disabled={approving !== null}
                        onClick={() => toggleApproval(quote, true)}>
                        {approving === quote.id ? 'Approving…' : 'Approve'}
                      </Button>
                    ) : (
                      <Chip label='Awaiting admin approval' size='small' color='warning' variant='outlined' />
                    )}
                  </TableCell>
                  <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                    {PDF_VERSIONS.map((version) => (
                      <Tooltip key={version.key} title={version.tooltip}>
                        {/* Spans the button so the tooltip still shows while it is disabled. */}
                        <Box component='span'>
                          <IconButton
                            size='small'
                            color='primary'
                            disabled={opening !== null}
                            onClick={() => viewPdf(quote, version.withLetterhead)}
                            aria-label={`${version.tooltip}, raised ${formatWhen(quote.createdAt)}`}
                          >
                            {opening?.id === quote.id && opening.withLetterhead === version.withLetterhead ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Icon icon={version.icon} width={20} />
                            )}
                          </IconButton>
                        </Box>
                      </Tooltip>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <WorkOrderDialog
        quote={workOrderFor}
        onClose={() => setWorkOrderFor(null)}
        onSaved={() => {
          setWorkOrderFor(null);
          onChanged?.();
        }}
      />

      <QuotationPdfViewer
        file={viewing?.file ?? null}
        title={
          viewing
            ? `Quotation raised ${formatWhen(viewing.quote.createdAt)}${
                viewing.quote.quoteAsPer ? ` · ${viewing.quote.quoteAsPer}` : ''
              }${viewing.withLetterhead ? '' : ' · without letterhead'}`
            : ''
        }
        onClose={closeViewer}
      />
    </Box>
  );
}

/**
 * The client's work order against one quotation (V36). Saving it tells every admin the
 * quotation is ready for approval; until then it can be corrected here.
 */
function WorkOrderDialog({
  quote,
  onClose,
  onSaved,
}: {
  quote: JobQuote | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [number, setNumber] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!quote) return;
    setNumber(quote.workOrderNo ?? '');
    setDate(quote.workOrderDate ?? dayjs().format('YYYY-MM-DD'));
    setError(null);
  }, [quote]);

  const today = dayjs().format('YYYY-MM-DD');
  const invalid = !number.trim() ? 'Enter the work order number.' : date && date > today ? 'The work order cannot be dated in the future.' : null;

  async function save() {
    if (!quote || invalid) return;
    setSaving(true);
    setError(null);
    try {
      await setJobQuoteWorkOrder(quote.id, number.trim(), date || null);
      toast.success('Work order added — the admin has been asked to approve the quotation');
      onSaved();
    } catch (err) {
      setError(logError({ message: 'Could not save the work order', error: err, code: 'JOB_QUOTE_WORK_ORDER_FAILED' }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={Boolean(quote)} onClose={saving ? undefined : onClose} maxWidth='xs' fullWidth>
      <DialogTitle>{quote?.workOrderNo ? 'Correct work order' : 'Add work order'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <TextField
            label='Work order no.'
            required
            size='small'
            autoFocus
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            label='Work order date'
            type='date'
            size='small'
            value={date}
            onChange={(e) => setDate(e.target.value)}
            inputProps={{ max: today }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Typography variant='caption' color='text.secondary'>
            It prints on the quotation PDF and on bills raised from it. The admin approves the quotation once it is added.
          </Typography>
          {error && <Typography variant='body2' color='error'>{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || Boolean(invalid)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
